import { Box, GCategories, IconArrow, IconButton } from '@gazin/ecommerce-ui'
import { memo, useMemo, useRef } from 'react'
import { theme } from 'src/styles/themes/default'
import { useWindowDimensions } from 'src/contexto/hooks/useWindowDimensions'
import { ThumborProportionLoader } from 'src/util/thumborLoader'
import { Navigation, Pagination } from 'swiper/modules'
import { Swiper, SwiperProps, SwiperSlide } from 'swiper/react'

import 'swiper/css'
import 'swiper/css/bundle'
import 'swiper/css/navigation'
import 'swiper/css/pagination'

type Category = {
  value: string
  description: string
  media?: Media
}

type Media = {
  desktop?: string | null
  mobile?: string | null
}

type CategoryCarouselProps = {
  categories: Category[]
  textColor: string
  backgroundColor: string
  isBlackFriday?: boolean
  pagination?: boolean
  onClickItem?: (category: string) => void
}

export const CategoryCarouselComponent = ({
  categories,
  textColor,
  backgroundColor,
  isBlackFriday,
  pagination = false,
  onClickItem,
}: CategoryCarouselProps) => {
  const swiperRef = useRef<any>(null)
  const { width } = useWindowDimensions()
  const isMobile = width! <= 768
  const isTablet = width! > 768 && width! <= 992

  const params: SwiperProps = {
    modules: [Pagination, Navigation],
    slidesPerView: 6,
    centeredSlides: false,
    centerInsufficientSlides: true,
    breakpoints: {
      1024: {
        slidesPerView: pagination ? 6 : 5,
      },
      768: {
        slidesPerView: 4,
      },
      530: {
        slidesPerView: 3.5,
      },
      320: {
        slidesPerView: 3.5,
      },
      0: {
        slidesPerView: 3.5,
      },
    },
    navigation: true,
    pagination: {
      clickable: true,
      type: 'bullets',
      enabled: pagination,
    },
    direction: 'horizontal',
    onBeforeInit: (swiper: any) => {
      swiperRef.current = swiper
    },
  }

  const getBackgroundSize = useMemo(() => {
    if (isMobile) return '4.375rem'
    if (isTablet) return '5.625rem'

    return '7.5rem'
  }, [isMobile, isTablet])

  const getImageSize = useMemo(() => {
    if (isMobile) return 0.7
    if (isTablet) return 0.875

    return 1.1
  }, [isMobile, isTablet])

  const categoriesWithDesktopAndMobileImage = useMemo(() => {
    return categories.filter(({ media }) => media?.desktop && media.mobile)
  }, [categories])

  return (
    <Box
      data-testid="category-carousel"
      position="relative"
      css={`
        .swiper {
          padding-top: 0.625rem;
          padding-bottom: ${pagination && !isMobile ? '1.625rem' : '0rem'};
        }
        .swiper-slide {
          padding-top: 1.25rem;
        }
        .swiper-pagination {
          opacity: 1;
        }
        .swiper-hidden {
          display: none;
        }
        .swiper-button-next,
        .swiper-button-prev {
          opacity: 0;
        }
        .swiper-pagination-bullets {
          bottom: 0px;
          left: 0px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .swiper-pagination-bullet {
          width: 16px;
          height: 3px;
          border-radius: 0px;
          opacity: 1;
          background: #66a9ff;
        }
        .swiper-pagination-bullet-active {
          width: 16px;
          height: 3px;
          border-radius: 0px;
          background: #0d71f0;
          opacity: 1;
        }
        @media (max-width: 992px) {
          .swiper-pagination {
            opacity: 0;
          }
        }
      `}
    >
      <Swiper
        {...params}
        ref={swiperRef}
        className={
          !categoriesWithDesktopAndMobileImage.length ? 'swiper-hidden' : ''
        }
      >
        {categories?.map(({ value, description, media }) => {
          return (
            <SwiperSlide
              key={value}
              className={
                !media?.desktop || !media?.mobile ? 'swiper-hidden' : ''
              }
            >
              <GCategories
                opacity={isBlackFriday ? 1 : 0.5}
                top={'-16px !important'}
                fontSize={isMobile ? '.625rem' : '.75rem'}
                title={description}
                backgroundSize={getBackgroundSize}
                colorText={
                  isBlackFriday ? '#fff' : textColor || theme.colors.blue[500]
                }
                variant={
                  isBlackFriday
                    ? '#8a8a8a'
                    : backgroundColor || theme.colors.blue[500]
                }
                urlImage={
                  media?.mobile
                    ? ThumborProportionLoader(media.mobile, getImageSize, 75)
                    : ''
                }
                path={!onClickItem ? `/categoria/${value}` : undefined}
                onClick={() => onClickItem?.(value)}
              />
            </SwiperSlide>
          )
        })}
      </Swiper>
      {pagination && categoriesWithDesktopAndMobileImage.length > 6 && (
        <>
          <IconButton
            icon={
              <IconArrow
                color="#0D71F0"
                transform="rotate(-180deg)"
                w="0.875rem"
                h="0.875rem"
              />
            }
            variant="unstyled"
            display={{ base: 'none', lg: 'flex' }}
            justifyContent="center"
            aria-label="prev-button"
            position="absolute"
            bg="neutral.75"
            left="-3.5rem"
            top="38%"
            borderRadius="100%"
            zIndex={2}
            width="42px"
            height="42px"
            bgSize={42}
            onClick={() => swiperRef.current?.slidePrev()}
          />
          <IconButton
            icon={<IconArrow color="#0D71F0" w="0.875rem" h="0.875rem" />}
            display={{ base: 'none', lg: 'flex' }}
            justifyContent="center"
            variant="unstyled"
            aria-label="next-button"
            position="absolute"
            bg="neutral.75"
            right="-3.5rem"
            top="38%"
            zIndex={2}
            borderRadius="100%"
            width="42px"
            height="42px"
            bgSize={42}
            onClick={() => swiperRef.current?.slideNext()}
          />
        </>
      )}
    </Box>
  )
}

export const CategoryCarousel = memo(CategoryCarouselComponent)
